/**
 * RunAI OS — Dock
 * Taşınabilir: bottom/top/left/right
 * Sağ tıkla pozisyon değiştir
 */
import { Suspense, useState } from 'react'
import { useWindowManager } from '../WindowManager/WindowManagerContext'
import { WINDOW_REGISTRY } from '../WindowManager/windowRegistry'
import { DOCK_ITEMS } from './dockConfig'
import DockItem from './DockItem'
import { useDockPosition, type DockPosition } from './useDockPosition'

const POSITIONS: { label: string; value: DockPosition }[] = [
  { label: '⬇ Bottom', value: 'bottom' },
  { label: '⬆ Top',    value: 'top' },
  { label: '⬅ Left',   value: 'left' },
  { label: '➡ Right',  value: 'right' },
]

export default function Dock() {
  const { openWindow, windows } = useWindowManager()
  const { position, setPos, isHorizontal } = useDockPosition()
  const [showMenu, setShowMenu] = useState(false)

  const isActive = (id: string) => windows.some(w => w.id === id && !w.minimized)

  const handleClick = (id: string, path: string, title: string, width = 860, height = 580, noPadding = false) => {
    const Component = WINDOW_REGISTRY[path]
    if (!Component) { return }

    const vw = window.innerWidth
    const vh = window.innerHeight
    const w = Math.min(width, Math.floor(vw * 0.85))
    const h = Math.min(height, Math.floor(vh * 0.80))
    const openCount = windows.length
    const x = Math.max(20, Math.floor((vw - w) / 2) + openCount * 20)
    const y = Math.max(20, Math.floor((vh - h) / 2) + openCount * 20)

    const WrappedComponent = () => (
      <Suspense fallback={<div style={{ padding: '40px', textAlign: 'center', color: 'var(--os-muted)', fontSize: '13px' }}>Loading...</div>}>
        <Component />
      </Suspense>
    )

    openWindow({ id, title, component: WrappedComponent, position: { x, y }, size: { width: w, height: h }, noPadding })
  }

  // Dock container style based on position
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--os-dock-bg)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    flexShrink: 0,
    position: 'relative',
    ...(position === 'bottom' ? { height: '72px', borderTop: '1px solid var(--os-border)', width: '100%' } :
       position === 'top'    ? { height: '72px', borderBottom: '1px solid var(--os-border)', width: '100%' } :
       position === 'left'   ? { width: '72px', borderRight: '1px solid var(--os-border)', height: '100%', flexDirection: 'column' } :
                               { width: '72px', borderLeft: '1px solid var(--os-border)', height: '100%', flexDirection: 'column' }),
  }

  const pillStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: isHorizontal ? '8px 14px' : '14px 8px',
    background: 'var(--os-surface)',
    borderRadius: '18px',
    border: '1px solid var(--os-border)',
    flexDirection: isHorizontal ? 'row' : 'column',
  }

  return (
    <div
      style={containerStyle}
      onContextMenu={e => { e.preventDefault(); setShowMenu(prev => !prev) }}
    >
      {/* Position context menu */}
      {showMenu && (
        <div style={{
          position: 'absolute',
          ...(position === 'bottom' ? { bottom: '80px', left: '50%', transform: 'translateX(-50%)' } :
             position === 'top'    ? { top: '80px', left: '50%', transform: 'translateX(-50%)' } :
             position === 'left'   ? { left: '80px', top: '50%', transform: 'translateY(-50%)' } :
                                     { right: '80px', top: '50%', transform: 'translateY(-50%)' }),
          background: 'var(--os-win-bg)',
          border: '1px solid var(--os-border)',
          borderRadius: '10px',
          padding: '6px',
          zIndex: 9999,
          backdropFilter: 'blur(20px)',
          boxShadow: 'var(--os-win-shadow)',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          minWidth: '120px',
        }}>
          <div style={{ fontSize: '10px', color: 'var(--os-muted)', padding: '4px 8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Dock Position
          </div>
          {POSITIONS.map(p => (
            <button
              key={p.value}
              onClick={() => { setPos(p.value); setShowMenu(false) }}
              style={{
                padding: '6px 10px',
                background: position === p.value ? 'rgba(99,102,241,0.12)' : 'transparent',
                border: 'none',
                borderRadius: '6px',
                color: position === p.value ? '#818cf8' : 'var(--os-text)',
                fontSize: '12px',
                cursor: 'pointer',
                textAlign: 'left',
                fontWeight: position === p.value ? 600 : 400,
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}

      <div style={pillStyle}>
        {DOCK_ITEMS.map(item => {
          if (item.separator) {
            return (
              <div key={item.id} style={{
                ...(isHorizontal ? { width: '1px', height: '30px' } : { height: '1px', width: '30px' }),
                background: 'var(--os-border)',
                margin: isHorizontal ? '0 4px' : '4px 0',
              }} />
            )
          }
          return (
            <DockItem
              key={item.id}
              item={item}
              active={isActive(item.id)}
              onClick={() => handleClick(item.id, item.path, item.windowTitle ?? item.label, item.windowWidth, item.windowHeight, item.noPadding)}
            />
          )
        })}
      </div>
    </div>
  )
}
